import type { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import axios from "axios";
import { useClient } from "../state/client";
import { initialized } from "../utils/initializedUtil";
import type { CustomerInfo, DeepPartial, Nullish, ThemeId, Usage, WidgetUid } from "../types";
import { Scope } from "../types";
import type { BaseRuleCondition, FormVersion, RuleOperator, SaveMode } from "../state/formVersion";
import type { Dto, Integration } from "../integrations/types";
import { integrationsByType } from "../integrations/integrations";
import { toFieldConfigDto, toFormVersion, toFormVersionDto } from "../utils/mappers/formVersion";
import type { FieldConfig, VariableConfig } from "../fields/types";
import { LOCATION_BASE_URL, MINIMUM_ACTION_LOADING_DELAY } from "../constants";
import type { QueryClient } from "@tanstack/solid-query";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { hours, minutes, seconds } from "../utils/timeUtil";
import type { VariableType } from "../variables/types";
import type { Accessor } from "solid-js";
import type FormId from "../state/FormId";
import FormVersionId from "../state/FormVersionId";
import type CustomerId from "../state/CustomerId";
import { toFolder } from "../utils/mappers/folder";
import type FolderId from "../state/FolderId";
import type { JsonPatch } from "../types/JsonPatch";
import type { CreateFolder, Folder } from "../types/Folder";
import type { MoreAppError } from "../MoreAppError";
import { handleApiError } from "../MoreAppError";
import { toLegacyPlatformIcon } from "../utils/iconUtil";
import type { Form } from "../state/form";
import { toForm } from "../utils/mappers/form";
import { first, isNil, merge } from "lodash-es";
import { compareByLocale } from "../utils/stringUtil";
import { toToken } from "../utils/mappers/token";
import type { CopyForm, CreateForm } from "../types/Form";
import { toSubmission } from "../utils/mappers/submission";
import pMinDelay from "p-min-delay";
import { doNotRetryOnUnrecoverableError } from "../utils/apiUtil";
import type SubmissionId from "../state/SubmissionId";
import { toHookPipeline, toHookPipelineInfo } from "../utils/mappers/hookPipeline";
import type { MailStatuses, SubmissionIntegrationStatus } from "../types/Submission";
import type { SecureUriDto, UploadCustomerResourceDto } from "./CustomerResourceService";
import type { ExportField } from "../types/ExportField";
import { type Filter, toApi } from "../pages/customer/form/filter";
import type { LanguageCode } from "../state/i18n";
import { toTemplate } from "../utils/mappers/template";
import type TemplateId from "../state/TemplateId";
import type ApiKeyId from "../state/ApiKeyId";
import { toApiKey } from "../utils/mappers/apiKey";
import type { ApiKey } from "../types/ApiKey";
import type { SSoConnection, SsoStatus } from "../types/Sso";
import { toSsoStatus } from "../utils/mappers/ssoStatus";
import { toTask } from "../utils/mappers/task";
import { toEmailConfiguration } from "../utils/mappers/emailConfiguration";
import type { ApiCoordinates, LocationConfiguration, LocationResult, ReversedResult } from "../types/Location";
import { locationToHumanReadableString } from "../utils/locationUtil";
import type { EmailConfiguration } from "../types/EmailConfiguration";
import type { Webhook, WebhookInvocation } from "../types/Webhook";
import { toWebhook } from "../utils/mappers/webhook";
import type WebhookId from "../state/WebhookId";
import type TaskId from "../state/TaskId";
import CustomerService from "./CustomerService";
import { toCustomerInfo } from "../utils/mappers/customerInfo";
import { toWebhookInvocation } from "../utils/mappers/webhookInvocation";
import type EventId from "../state/EventId";
import { toEvent } from "../utils/mappers/event";
import type { Event } from "../types/Event";
import type { TaskRecipient } from "../types/Task";
import { toDataSource } from "../utils/mappers/dataSource";
import type DataSourceId from "../state/DataSourceId";
import type { DataSource } from "../types/DataSource";
import type { UrlDataSourceConfigurationDto } from "../pages/customer/dataSource/UrlDataSource";
import type { GoogleSheetsDataSourceConfigurationDto } from "../pages/customer/dataSource/GoogleSheetsDataSource";
import { toDataSourceEntry } from "../utils/mappers/dataSourceEntry";

const PAGINATION_DEFAULTS = {
  page: 0,
  size: 10,
};

export type PaginatedResponse<T> = {
  totalSize: number;
  elements: T[];
};

export type PaginatedRequestOptions = {
  pagination?: Pagination;
  query?: Query[];
  sort?: Sort[];
};

export type SubmissionDto = {
  id: string;
  info: {
    userId: string;
    date: number;
    summary: string;
  };
  data: Record<string, unknown>;
  meta: {
    serialNumber: number;
    registrationDate: string;
    taskId?: string;
    location?: {
      latitude: string;
      longitude: string;
    };
    device: {
      name: string;
      network: string;
      appVersion?: string;
    };
  };
  mailStatuses: MailStatuses[];
};

export type TaskStatusDto = "IN_PROGRESS" | "COMPLETED" | "REVOKED" | "DECLINED";

export type TaskLocationCoordinatesDto = {
  latitude: number;
  longitude: number;
};

export type TaskLocationAddressDto = {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  houseNumber?: string;
};

export type TaskLocationDto = {
  coordinates: TaskLocationCoordinatesDto;
  address: TaskLocationAddressDto | null;
  formattedValue: string;
};

export type TaskUserDto = {
  userId: string;
  username: string;
};

export type TaskFulfilmentDto = {
  userId: string;
  date: string; // ISO 8601 date string
  formId: string;
  registrationId: string;
};

export type TaskDatesDto = {
  creationDate: string; // ISO 8601 date string
  publishDate: string; // ISO 8601 date string
  informationDate: string | null;
};

export type UserCreatorDto = {
  type: "USER";
  id?: string;
  name?: string;
};

export type ApiKeyCreatorDto = {
  type: "API_KEY";
  id?: string;
  name?: string;
};

export type IntegrationCreatorDto = {
  type: "INTEGRATION";
  id?: string;
  name?: string;
};

export type UnknownCreatorDto = {
  type: "UNKNOWN";
};

export type TaskCreatorDto = UserCreatorDto | ApiKeyCreatorDto | IntegrationCreatorDto | UnknownCreatorDto;

export type TaskDto = {
  id: string;
  customerId: number;
  formId: string;
  formName: string;
  description: string | null;
  formIcon: string;
  formIconColor: string;
  users: string[];
  createdBy: TaskCreatorDto;
  message: string | null;
  dates: TaskDatesDto;
  data: Record<string, unknown>;
  status: TaskStatusDto;
  fulfilments: TaskFulfilmentDto[];
  location: TaskLocationDto | null;
  taskUsers: TaskUserDto[];
};

export type Token = {
  customerId: CustomerId;
  formId: FormId;
  token: string;
};

export type TokenDto = {
  customerId: number;
  formId: string;
  token: string;
};

export type Pagination = {
  page?: number;
  size?: number;
};

export type Query = {
  path: string;
  type: "string" | "number" | "boolean" | "date" | "in" | "exists";
} & (StringQuery | NumberQuery | BooleanQuery | DateQuery | InQuery | ExistsQuery);

type StringQuery = {
  type: "string";
  value: string;
};

type NumberQuery = {
  type: "number";
  value: {
    match: "eq" | "neq" | "lte" | "lt" | "gte" | "gt";
    value: number;
  };
};

type BooleanQuery = {
  type: "boolean";
  value: boolean;
};

type DateQuery = {
  type: "date";
  value: {
    start?: string | number;
    end?: string | number;
  };
};

type InQuery = {
  type: "in";
  value: string[];
};

type ExistsQuery = {
  type: "exists";
  value: boolean;
};

export const SortDirection = {
  Desc: -1,
  Asc: 1,
} as const;

export type SortDirection = (typeof SortDirection)[keyof typeof SortDirection];

type Sort = {
  key: string;
  direction: SortDirection;
};

export type UsageDto = {
  day: `${number}-${number}-${number}`; // 2025-12-01
  count: number;
};

export type FolderDto = {
  id: string;
  forms: FormDto[];
  meta: {
    name: string;
    description?: string;
    icon: string;
    image?: string;
    applicationId?: string;
  };
  status: "ACTIVE" | "HIDDEN" | "UNKNOWN";
};

export type HookPipelineInfoDto = {
  registrationId: string;
  status: string;
};

export type HookPipelineDto = {
  status: string;
  nextExecution: string | null;
  registrationId: string;
  steps: HookPipelineStepDto[];
};

export type HookPipelineStepDto = {
  status: string;
  configuration: HookPipelineStepConfigurationDto;
  executionCount: number;
  response: Record<string, unknown>;
};

export type HookPipelineStepConfigurationDto = {
  uid: string;
  namespace: string;
  key: string;
  version: number;
  hookName: string;
  configuration: Record<string, unknown>;
};

type CreateFolderRequest = {
  meta: {
    name: string;
    description?: string;
    icon: string;
  };
};

type CreateFormRequest = {
  meta: {
    name: string;
    description?: string;
    icon: string;
    tags?: string[];
    templateId?: TemplateId;
  };
};

export type ReorderFormRequest = { folderId: FolderId; formId: FormId; toPosition: number };

export type TemplateDto = {
  id: string;
  publishedVersion?: FormPublishedVersionDto;
  meta: FormMetaDto;
  status: "ACTIVE" | "HIDDEN" | "TRASH" | "UNKNOWN";
  scope: "GLOBAL" | "CUSTOMER";
};

export type FormMetaDto = {
  description?: string;
  icon: string;
  iconColor: string;
  name: string;
  language: string;
  tags: string[];
};

export type FormPublishedVersionDto = {
  formVersion?: string;
  publishedBy: string;
  publishedDate: Date;
};

export type FormLatestVersionDto = {
  id: string;
  lastUpdated: Date;
  lastUpdatedBy: string;
  status: string;
};

export type FormDto = {
  id: string;
  meta: FormMetaDto;
  publishedVersion?: FormPublishedVersionDto;
  latestVersion?: FormLatestVersionDto;
  status: "ACTIVE" | "HIDDEN" | "TRASH" | "UNKNOWN";
};

export type SubformDto = {
  uid: string;
  meta?: {
    // The whole 'meta' object is deleted when a form is copied in the old platform
    name?: string; // Name can be absent when a pin subform has been converted to linked in old platform
  };
  triggers?: TriggerDto[];
} & AbstractFormVersionDto;

type FormVersionMetaDto = {
  created: string;
  createdBy: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  lastUpdatedWith: string;
  status: "DRAFT" | "FINAL";
};

export type RuleDto = {
  name?: string;
  type: RuleOperator;
  conditions: RuleConditionDto[];
  actions: RuleActionDto[];
};

type ReferenceConditionDto = BaseRuleCondition & {
  fieldUid: string;
  type: "FIELD";
};

type DataSourceFieldConditionDto = BaseRuleCondition & {
  fieldUid: string;
  type: "FIELD_DATA_SOURCE";
  fieldObjectKey: string;
};

type UserConditionDto = BaseRuleCondition & {
  type: "USER";
};
export type EmptyCondition = {
  type?: null;
};
export type RuleConditionDto = ReferenceConditionDto | DataSourceFieldConditionDto | UserConditionDto | EmptyCondition;

export type RuleActionKeyDto = "value" | "visible";

export type RuleActionDto = {
  fieldUid?: string;
  key?: RuleActionKeyDto;
  value?: boolean | string;
};

export type TriggerDto = {
  type: string;
  name: string;
  staticRecipients: string[];
  dynamicRecipients: string[];
  carbonCopyRecipients?: string[];
  blindCarbonCopyRecipients?: string[];
  subject: string;
  content: string;
  pdfContent: Nullish<string>;
  pdfFilename: Nullish<string>;
  fromName: Nullish<string>;
  replyTo: Nullish<string>;
  pdfHeaderLogoResourceId?: string;
  configuration: {
    attachments: string[];
    attachImages: boolean;
    embedImages: boolean;
    attachPdf: boolean;
    simpleMode: boolean;
    useHeaderAndFooter: boolean;
    hideNoValues: boolean;
    horizontalOrientation: boolean;
    distributedGeneration: boolean;
    legacyGeneration: boolean;
  };
};
export type IntegrationDto = {
  uid: string;
  namespace: string;
  key: string;
  name: string;
  version: number;
  configuration: Record<string, unknown>;
};

export type FormVersionDto = {
  id: string;
  formId: string;
  triggers?: TriggerDto[];
  integrations?: IntegrationDto[];
  meta: FormVersionMetaDto;
  settings: {
    saveMode: SaveMode;
    itemHtml?: string;
    searchSettings: {
      enabled?: boolean;
      onlyForCurrentUser?: boolean;
      filteringEnabled?: boolean;
      filteredFields?: string[];
      fields?: Record<string, boolean>;
    };
  };
  theme: {
    id: ThemeId;
  };
  dependencies?: [{ type: "DATASOURCE" | "RESOURCE" | "URL" | "SERVICE_ACCOUNT"; value: string }];
  fieldProperties: Record<string, SubformDto>;
  // DEV-6710 Remove nullish and always expect a list
  variables: Nullish<VariableConfigDto[]>;
} & AbstractFormVersionDto;

export type AbstractFormVersionDto = {
  fields: FieldConfigDto[];
  rules: RuleDto[];
};

export type FieldConfigDto = {
  widget: WidgetUid;
  uid: string;
  properties: Record<string, unknown>;
};

export type VariableConfigDto = {
  uid: string;
  name: string;
  dataName: string;
  type: VariableType;
  defaultValue: unknown;
};

type IntegrationVerificationRequest<T> = {
  configuration: {
    uid: string;
    namespace: string;
    key: string;
    version: number;
    configuration: T;
  };
  view: FormVersionDto;
};

export type IntegrationVerificationResultDto<T> =
  | {
      status: "VALID";
    }
  | {
      status: "INVALID";
      fieldErrors?: Record<keyof T, unknown>;
      globalError?: string;
    }
  | {
      status: "NOT_VALIDATED";
    };

export type ApiKeyDto = {
  id: string;
  customerId: number;
  name: string;
  lastCharacters: string;
  created: string;
};

export type ApiKeyCreatedDto = ApiKeyDto & { token: string };

export type CreateApiKeyRequest = {
  name: string;
};

export type WebhookDto = {
  id: string;
  name: string;
  url: string;
  type: string[];
  status: string;
  secret: string;
};

export type WebhookInvocationDto = {
  id: string;
  subscriberId: string;
  eventId: string;
  type: string;
  customerId: number;
  status: string;
  statusCode: number;
  message: string;
  invokedOn: string; // 2021-06-15T08:13:21.557Z
  retryOn?: string; // 2021-06-15T08:13:21.557Z
  attempt: number;
};

export type EventDto = {
  id: string;
  timestamp: string; // 2021-06-15T08:13:21.557Z
  customerId: number;
  type: string;
  data: Record<string, unknown>;
};

export type CreateWebhookRequest = {
  name: string;
  url: string;
  type: string[];
  status: string;
};
export type UpdateWebhookRequest = CreateWebhookRequest & { id: string };

export type SsoStatusDto = {
  sso: SSoConnection | null;
  dsync: SSoConnection | null;
  provisioned: boolean;
  shared: boolean;
};

type DnsRecordStatus = "verified" | "unverified";
export type EmailConfigurationDto = {
  domain: string;
  shared: boolean;
  fromName: string;
  fromEmail: string;
  records: {
    sending: {
      type: string;
      hostname: string;
      value: string;
      status: DnsRecordStatus;
    };
    bounce: {
      type: string;
      hostname: string;
      value: string;
      status: DnsRecordStatus;
    };
  };
};

export type CreateEmailConfigurationRequest = { domain: string; fromName: string; fromEmail: string };
export type UpdateEmailConfigurationRequest = { fromName: string; fromEmail: string };

export type PublishInfoType = "IMMEDIATE" | "RELATIVE" | "ABSOLUTE";
export type CreateTaskRequest = {
  formId: FormId;
  recipients: TaskRecipient[];
  message: string;
  publishInfo: { type: PublishInfoType; value: number };
  informationDate: number | undefined;
  location: LocationResult | null;
  registrationData: Record<string, unknown>;
};

export type ColumnMappingDto = {
  id: string;
  label: string;
};
export type DataSourceInfoDto = {
  id: string;
  name: string;
  columnMapping: ColumnMappingDto[];
};
export type DataSourceDto = {
  id: string;
  name: string;
  enabled: boolean;
  lastUpdated?: number;
  updateStatus: string;
  lastUpdateWarningMessages: string[];
  urlConfiguration?: UrlDataSourceConfigurationDto["urlConfiguration"];
  googleConfiguration?: GoogleSheetsDataSourceConfigurationDto["googleConfiguration"];
  columnMapping: { id: string; label: string }[];
  searchType: string;
  failedExecutionCount: number;
};

export type DataSourceEntryDto = {
  id: string;
  dataSourceId: string;
  version: string;
  data: Record<string, unknown>;
};

export type UpdateCustomerSettingsRequest = {
  dateFormat: string;
  segment: string;
  otherSegment?: string;
};

export type ScheduleCustomerDeletionRequest = { terminationReasons: string[]; message: string };

let client: AxiosInstance | null = null;
let customerId: CustomerId | null = null;

const init = (cusId: CustomerId): void => {
  customerId = cusId;
  client = useClient();
};

const getClient = () => initialized(client, "client");

const getFoldersByCustomerId = (cusId: CustomerId | null) =>
  useQuery(() => ({
    queryKey: ["folders", cusId],
    queryFn: async () => {
      const { data } = await getClient().get<FolderDto[]>(`api/v1.0/forms/customer/${cusId}/folders`);
      return data.map(toFolder).sort((a, b) => compareByLocale(a.meta.name, b.meta.name));
    },
    staleTime: minutes(10),
    throwOnError: true,
  }));

const dataSourceInfosQueryOptions = (formId: FormId) => ({
  queryKey: ["dataSourceInfos", formId.toString()],
  queryFn: async () => {
    try {
      const { data } = await getClient().get<DataSourceInfoDto[]>(
        `/api/v1.0/customers/${customerId}/forms/${formId}/datasources`,
      );
      return data;
    } catch {
      return [] as DataSourceInfoDto[];
    }
  },
  staleTime: minutes(1),
});

const createTaskMutation = (action: "complete" | "revoke") => {
  const queryClient = useQueryClient();
  return useMutation<void, MoreAppError, { formId: FormId; taskId: TaskId }>(() => ({
    mutationFn: async ({ formId, taskId }) => {
      await getClient().post(`api/v1.0/customers/${customerId}/${formId}/tasks/${taskId}/${action}`);
    },
    onSuccess: (_, { formId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["forms", formId.toString(), "tasks"],
        exact: false,
      });
    },
  }));
};

const ApiService = {
  getCustomers: () =>
    useQuery<CustomerInfo[]>(() => ({
      queryKey: ["customers"],
      queryFn: async () => (await CustomerService.getCustomers()).map(toCustomerInfo),
      staleTime: minutes(10),
    })),
  getTemplates: () =>
    useQuery(() => ({
      queryKey: ["templates"],
      queryFn: async () => {
        // This also returns templates created within the passed customerId, not only global ones
        const { data } = await getClient().get<TemplateDto[]>(`api/v1.0/forms/customer/${customerId}/templates`, {
          params: { scope: "GLOBAL" },
        });
        return data.map((dto) => toTemplate(dto, undefined)).sort((a, b) => compareByLocale(a.meta.name, b.meta.name));
      },
      staleTime: minutes(10),
      throwOnError: true,
    })),
  getDailyUsage: (from: Date, until: Date) =>
    useQuery<Usage>(() => ({
      queryKey: ["usage", from, until],
      queryFn: async () => {
        const { data } = await getClient().get<UsageDto[]>(
          `api/v1.0/customers/${customerId}/usage?from=${from.getTime()}&until=${until.getTime()}`,
        );

        return {
          total: data.reduce((acc, cur) => acc + cur.count, 0),
          data: data.map((usage) => ({
            x: new Date(usage.day),
            y: usage.count,
          })),
        };
      },
      staleTime: minutes(1),
      throwOnError: true,
    })),
  getFolders: () => getFoldersByCustomerId(customerId),
  getFoldersByCustomerId: getFoldersByCustomerId,
  getSubmissions: (formId: FormId, options?: PaginatedRequestOptions) => {
    const mergedOptions = merge({}, { pagination: PAGINATION_DEFAULTS, query: [], sort: [] }, options);

    return useQuery(() => ({
      queryKey: ["submissions", formId.toString(), mergedOptions],
      queryFn: async () => {
        const { data } = await pMinDelay(
          getClient().post<PaginatedResponse<SubmissionDto>>(
            `api/v1.0/customers/${customerId}/forms/${formId}/submissions/filter/${mergedOptions.pagination.page}`,
            {
              pageSize: mergedOptions.pagination.size,
              query: mergedOptions.query,
              sort: mergedOptions.sort,
            },
          ),
          MINIMUM_ACTION_LOADING_DELAY,
          { delayRejection: false },
        );

        const hookPipelineInfos = await getClient()
          .post<HookPipelineInfoDto[]>(
            `api/v1.0/customers/${customerId}/forms/${formId}/hookpipeline`,
            data.elements.map((element) => element.id),
          )
          .then((result) => result.data.map(toHookPipelineInfo));

        return {
          totalSize: data.totalSize,
          elements: data.elements.map((dto) =>
            toSubmission(
              dto,
              (hookPipelineInfos.find((info) => info.submissionId.equals(dto.id))?.status ??
                "OPEN") as SubmissionIntegrationStatus,
            ),
          ),
        };
      },
      staleTime: seconds(10),
    }));
  },
  deleteSubmissions: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { submissionIds: SubmissionId[]; formId: FormId }>(() => ({
      mutationFn: async ({ submissionIds }) => {
        const deletePromises = submissionIds.map((submissionId) =>
          getClient().delete(`api/v1.0/customers/${customerId}/registrations/${submissionId}`),
        );
        await Promise.all(deletePromises);
      },
      onSuccess: async (_, { formId }) => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["submissions", formId.toString()] }),
          queryClient.invalidateQueries({ queryKey: ["forms", formId.toString(), "tasks"] }),
        ]);
      },
    }));
  },
  getTasks: (formId: FormId, options?: PaginatedRequestOptions) => {
    const mergedOptions = merge({}, { pagination: PAGINATION_DEFAULTS, query: [], sort: [] }, options);

    return useQuery(() => ({
      queryKey: ["forms", formId.toString(), "tasks", mergedOptions],
      queryFn: async () => {
        const { data } = await pMinDelay(
          getClient().post<PaginatedResponse<TaskDto>>(
            `api/v1.0/customers/${customerId}/${formId}/tasks/filter/${mergedOptions.pagination.page}`,
            {
              pageSize: mergedOptions.pagination.size,
              query: mergedOptions.query,
              sort: mergedOptions.sort,
            },
          ),
          MINIMUM_ACTION_LOADING_DELAY,
          { delayRejection: false },
        );

        return {
          totalSize: data.totalSize,
          elements: data.elements.map(toTask),
        };
      },
      staleTime: seconds(10),
    }));
  },
  createDataSource: () => {
    const queryClient = useQueryClient();
    return useMutation<DataSource, MoreAppError, Record<string, unknown>>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().post<DataSourceDto>(`api/v1.0/customers/${customerId}/datasources`, value);
        return toDataSource(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["dataSources"] });
        void queryClient.invalidateQueries({ queryKey: ["dataSourceInfos"] });
      },
    }));
  },
  updateDataSource: (dataSourceId: DataSourceId) => {
    const queryClient = useQueryClient();
    return useMutation<DataSource, MoreAppError, Record<string, unknown>>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().put<DataSourceDto>(
          `api/v1.0/customers/${customerId}/datasources/${dataSourceId}`,
          value,
        );
        return toDataSource(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["dataSources"] });
        void queryClient.invalidateQueries({ queryKey: ["dataSourceInfos"] });
      },
    }));
  },
  importDataSourceFile: async (dataSourceId: DataSourceId, file: { name: string; source: string }) => {
    const queryClient = useQueryClient();
    const formData = new FormData();
    const res = await axios.get(file.source, { responseType: "blob" });
    const blob = await res.data;

    formData.append("file", blob, file.name);
    formData.append("name", file.name);

    await getClient().post<UploadCustomerResourceDto>(
      `api/v1.0/customers/${customerId}/datasources/${dataSourceId}/importFile`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: seconds(90),
      },
    );
    void queryClient.invalidateQueries({ queryKey: ["dataSources", dataSourceId.toString()] });
  },
  getDataSourceEntries: (dataSourceId: DataSourceId, options?: PaginatedRequestOptions) => {
    const mergedOptions = merge({}, { pagination: PAGINATION_DEFAULTS, query: [], sort: [] }, options);

    return useQuery(() => ({
      queryKey: ["dataSources", dataSourceId.toString(), "entries", mergedOptions],
      queryFn: async () => {
        const { data } = await pMinDelay(
          getClient().post<{ totalElements: number; content: DataSourceEntryDto[] }>(
            `api/v1.0/customers/${customerId}/datasources/${dataSourceId}/entries/page/${mergedOptions.pagination.page}`,
            {
              pageSize: mergedOptions.pagination.size,
              query: mergedOptions.query,
              sort: mergedOptions.sort,
            },
          ),
          MINIMUM_ACTION_LOADING_DELAY,
          { delayRejection: false },
        );

        return {
          totalSize: data.totalElements,
          elements: data.content.map(toDataSourceEntry),
        };
      },
      staleTime: seconds(10),
    }));
  },
  revokeTask: () => createTaskMutation("revoke"),
  completeTask: () => createTaskMutation("complete"),
  deleteTask: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { formId: FormId; taskId: TaskId }>(() => ({
      mutationFn: async ({ formId, taskId }) => {
        await getClient().delete(`api/v1.0/customers/${customerId}/${formId}/tasks/${taskId}`);
      },
      onSuccess: (_, { formId }) => {
        void queryClient.invalidateQueries({ queryKey: ["forms", formId.toString(), "tasks"] });
      },
    }));
  },
  getHookPipeline: (formId: FormId, submissionId: SubmissionId) =>
    useQuery(() => ({
      queryKey: ["hookPipelines", submissionId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<HookPipelineDto>(
          `api/v1.0/customers/${customerId}/forms/${formId}/registrations/${submissionId}/hookpipeline`,
        );
        return toHookPipeline(data);
      },
      refetchInterval: seconds(2),
      throwOnError: true,
    })),
  getHookpipelineInfos: async (formId: FormId, submissionIds: SubmissionId[]) => {
    const { data } = await getClient().post<HookPipelineInfoDto[]>(
      `api/v1.0/customers/${customerId}/forms/${formId}/hookpipeline`,
      submissionIds.map((id) => id.toString()),
    );
    return data.map(toHookPipelineInfo);
  },
  skipHookpipelineStep: (action: "skip" | "retry") => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { formId: FormId; submissionId: SubmissionId }>(() => ({
      mutationFn: async (value) => {
        await getClient().get(
          `api/v1.0/customers/${customerId}/forms/${value.formId}/registrations/${value.submissionId}/hookpipeline/${action}`,
        );
      },
      onSuccess: (_, value) => {
        void queryClient.invalidateQueries({ queryKey: ["hookPipelines", value.submissionId.toString()] });
      },
    }));
  },
  resendSubmission: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, SubmissionId>(() => ({
      mutationFn: async (submissionId) => {
        await getClient().put(`api/v1.0/customers/${customerId}/submissions/resend/${submissionId}`);
      },
      onSuccess: (_, submissionId) => {
        void queryClient.invalidateQueries({ queryKey: ["hookPipelines", submissionId.toString()] });
      },
    }));
  },
  getFolder: (folderId: FolderId) =>
    useQuery(() => ({
      queryKey: ["folders", folderId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<FolderDto>(`api/v1.0/forms/customer/${customerId}/folders/${folderId}`);
        return toFolder(data);
      },
      staleTime: minutes(10),
      retry: doNotRetryOnUnrecoverableError,
    })),
  createFolder: () => {
    const queryClient = useQueryClient();
    return useMutation<Folder, MoreAppError, CreateFolder>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().post<FolderDto, AxiosResponse<FolderDto>, CreateFolderRequest>(
          `api/v1.0/forms/customer/${customerId}/folders`,
          {
            meta: {
              name: value.name,
              description: value.description,
              icon: toLegacyPlatformIcon(value.icon),
            },
          },
        );
        return toFolder(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  patchFolder: (folderId: FolderId) => {
    const queryClient = useQueryClient();
    return useMutation<Folder, MoreAppError, JsonPatch[]>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().patch<FolderDto, AxiosResponse<FolderDto>, JsonPatch[]>(
          `api/v1.0/forms/customer/${customerId}/folders/${folderId}`,
          value,
          { headers: { "Content-Type": "application/json-patch+json" } },
        );
        return toFolder(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  copyForm: () => {
    const queryClient = useQueryClient();
    return useMutation<FormDto, MoreAppError, CopyForm>(() => ({
      mutationFn: async ({ name, destinationCustomerId, formId, folderId, formVersionId, positionInFolder }) => {
        const { data } = await getClient().post<FormDto>(
          `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions/${formVersionId}/copy`,
          {
            folderId,
            formName: name,
            customerId: destinationCustomerId,
            positionInFolder,
          },
        );
        return data;
      },
      onSuccess: (_, { folderId }) => {
        void queryClient.invalidateQueries({ queryKey: ["folders", folderId.toString()] });
      },
    }));
  },
  deleteFolder: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, FolderId>(() => ({
      mutationFn: async (folderId) => {
        await getClient().delete(`api/v1.0/forms/customer/${customerId}/folders/${folderId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  getForm: async (formId: FormId) => {
    const { data } = await getClient().get<FormDto>(`api/v1.0/forms/customer/${customerId}/forms/${formId}`);
    return data;
  },
  createForm: () => {
    const queryClient = useQueryClient();
    return useMutation<Form, MoreAppError, CreateForm>(() => ({
      mutationFn: async (value) => {
        if (!isNil(value.templateId) && isNil(value.publishedFormVersion)) {
          throw new Error("Unable to retrieve formVersion of the template, publishedFormVersion is not defined");
        }

        const { data } = await getClient().post<FormDto, AxiosResponse<FormDto>, CreateFormRequest>(
          `api/v1.0/forms/customer/${customerId}/forms`,
          {
            meta: {
              name: value.name,
              description: value.description,
              icon: toLegacyPlatformIcon(value.icon),
              tags: value.tags,
              templateId: value.templateId,
            },
          },
        );

        const form = toForm(data, undefined);

        // Add form to folder
        await getClient().post(
          `api/v1.0/forms/customer/${customerId}/folders/${value.folderId}/forms/${form.id}?position=0`,
        );

        if (!isNil(value.templateId)) {
          // Retrieve published formVersion of the template
          const templateFormVersion = await getClient().get<FormVersionDto>(
            `api/v1.0/forms/customer/${customerId}/forms/templates/${value.publishedFormVersion}`,
          );
          // Create new formVersion for the created form
          await getClient().post(
            `api/v1.0/forms/customer/${customerId}/forms/${form.id}/versions`,
            templateFormVersion.data,
          );
          return form;
        }

        const blankFormVersion: FormVersion = {
          // Server overrides this value
          id: FormVersionId.random(),
          formId: form.id,
          // Server overrides this value
          meta: {
            created: new Date(),
            createdBy: "SYSTEM",
            lastUpdated: new Date(),
            lastUpdatedBy: "SYSTEM",
            status: "DRAFT" as const,
          },
          fields: [],
          variables: [],
          rules: [],
          integrations: [],
          subforms: [],
          settings: {
            saveMode: "SAVE_AND_CLOSE_ONLY",
            descriptionInApp: [],
            searchSettings: {
              enabled: false,
              allowSearchingAllSubmissions: false,
              filteringEnabled: false,
              filteredFields: [],
              fields: [],
            },
          },
          theme: {
            id: "5ee1f55f54e4e995cc284b74",
          },
        };
        await getClient().post(
          `api/v1.0/forms/customer/${customerId}/forms/${form.id}/versions`,
          toFormVersionDto(blankFormVersion),
        );

        return form;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  patchForm: (formId: FormId) => {
    const queryClient = useQueryClient();
    return useMutation<Form, MoreAppError, DeepPartial<FormDto>>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().patch<FormDto, AxiosResponse<FormDto>, DeepPartial<FormDto>>(
          `api/v1.0/forms/customer/${customerId}/forms/${formId}`,
          value,
        );
        return toForm(data, undefined);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
        void queryClient.invalidateQueries({ queryKey: ["forms", formId.toString()] });
      },
    }));
  },
  moveForm: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { fromFolderId: FolderId; toFolderId: FolderId; formId: FormId }>(() => ({
      mutationFn: async ({ toFolderId, formId }) => {
        await getClient().put(`api/v1.0/forms/customer/${customerId}/folders/${toFolderId}/forms/${formId}?position=0`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  deleteForm: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { folderId: FolderId; formId: FormId }>(() => ({
      mutationFn: async ({ formId }) => {
        await getClient().delete(`api/v1.0/forms/customer/${customerId}/forms/${formId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  reorderForm: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, ReorderFormRequest>(() => ({
      mutationFn: async ({ folderId, formId, toPosition }) => {
        await getClient().put(
          `api/v1.0/forms/customer/${customerId}/folders/${folderId}/forms/${formId}/move/${toPosition}`,
        );
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["folders"] });
      },
    }));
  },
  getFormVersions: (formId: FormId, options?: Pagination) => {
    const { page, size } = merge({}, PAGINATION_DEFAULTS, options);

    return useQuery(() => ({
      queryKey: ["forms", formId.toString(), "formVersions", { page, size }],
      queryFn: async () => {
        const { data } = await getClient().get<FormVersionDto[]>(
          `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions`,
          { params: { page, size } },
        );
        return data.map((formVersionDto) => toFormVersion(formVersionDto, false));
      },
      staleTime: minutes(10),
      throwOnError: true,
    }));
  },
  getFormVersionsInfinite: (formId: FormId, options?: Pagination) => {
    const { size } = merge({}, PAGINATION_DEFAULTS, options);

    return useInfiniteQuery(() => ({
      queryKey: ["forms", formId.toString(), "formVersionsInfinite"],
      queryFn: async ({ pageParam = 0 }) => {
        const { data } = await getClient().get<FormVersionDto[]>(
          `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions`,
          { params: { page: pageParam, size } },
        );
        return data.map((formVersionDto) => toFormVersion(formVersionDto, false));
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages, lastPageParam) =>
        lastPage.length === size ? lastPageParam + 1 : undefined,
      staleTime: minutes(10),
      throwOnError: true,
    }));
  },
  getFormVersionQuery: (formId: FormId, formVersionId: FormVersionId) =>
    useQuery(() => ({
      queryKey: ["formVersions", formVersionId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<FormVersionDto>(
          `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions/${formVersionId}`,
        );
        return toFormVersion(data, false);
      },
      staleTime: minutes(10),
      throwOnError: true,
    })),
  getFormVersion: async (formId: FormId, formVersionId: FormVersionId) => {
    const { data } = await getClient().get<FormVersionDto>(
      `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions/${formVersionId}`,
    );
    return data;
  },
  createFormVersion: async (formId: FormId, formVersion: FormVersionDto) => {
    const { data } = await getClient().post<FormVersionDto>(
      `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions`,
      formVersion,
    );
    return data;
  },
  getAuthenticationTokens: () =>
    useQuery(() => ({
      queryKey: ["authenticationTokens"],
      queryFn: async () => {
        const { data } = await getClient().get<TokenDto[]>(`api/v1.0/customers/${customerId}/authenticationtoken`);
        return data.map(toToken);
      },
    })),
  createAuthenticationToken: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { formId: FormId }>(() => ({
      mutationFn: async (body) => {
        await getClient().post(`api/v1.0/customers/${customerId}/authenticationtoken`, body);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["authenticationTokens"] });
      },
    }));
  },
  revokeAuthenticationToken: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, { formId: FormId }>(() => ({
      mutationFn: async (body) => {
        await getClient().post(`api/v1.0/customers/${customerId}/authenticationtoken/revoke`, body);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["authenticationTokens"] });
      },
    }));
  },
  postFormVersion: async (queryClient: QueryClient, formVersion: FormVersionDto) => {
    const { data } = await getClient().put<FormVersionDto>(
      `api/v1.0/forms/customer/${customerId}/forms/${formVersion.formId}/versions/${formVersion.id}`,
      formVersion,
    );

    void queryClient.invalidateQueries({ queryKey: ["folders"] });

    return data;
  },
  resolveFormDraftId: async (formId: FormId): Promise<FormVersionId> => {
    const { data } = await getClient().get<FormVersionDto[]>(
      `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions`,
      { params: { page: 0, size: 1 } },
    );

    const latestFormVersion = first(data);
    if (isNil(latestFormVersion)) {
      throw new Error("Form without a form version");
    } else if (latestFormVersion.meta.status === "DRAFT") {
      return FormVersionId.from(latestFormVersion.id);
    } else {
      const draft = await ApiService.createFormVersion(formId, latestFormVersion);
      return FormVersionId.from(draft.id);
    }
  },
  finalizeFormVersion: async (formVersionId: FormVersionId, formId: FormId) => {
    const { data } = await getClient().post<FormVersionDto>(
      `api/v1.0/forms/customer/${customerId}/forms/${formId}/versions/${formVersionId}/finalize`,
      { customerId, formId, formVersionId },
    );
    return data;
  },
  publishFormVersion: async (queryClient: QueryClient, formVersionId: FormVersionId, formId: FormId) => {
    const { data } = await getClient().patch<FormVersionDto>(`api/v1.0/forms/customer/${customerId}/forms/${formId}`, {
      publishedVersion: { formVersion: formVersionId },
    });

    void queryClient.invalidateQueries({ queryKey: ["folders"] });
    void queryClient.invalidateQueries({ queryKey: ["forms", formId.toString()] });

    return data;
  },
  verifyIntegration: async <T>(integration: Integration<T>, formVersion: FormVersion) => {
    const definition = integrationsByType[integration.type];

    const { data } = await getClient().post<
      IntegrationVerificationResultDto<T>,
      AxiosResponse<IntegrationVerificationResultDto<T>>,
      IntegrationVerificationRequest<Dto<typeof definition>>
    >(`api/v1.0/customers/${customerId}/forms/${formVersion.formId}/integrations`, {
      configuration: {
        uid: integration.id.toString(),
        namespace: definition.legacy.namespace,
        key: definition.legacy.key,
        version: definition.legacy.version,
        configuration: definition.toDto(
          integration.configuration,
          formVersion.fields.filter((field) => field.scope === Scope.ROOT),
          formVersion.variables,
        ),
      },
      view: toFormVersionDto(formVersion),
    });
    return data;
  },
  generateDefaultPdfContent: async (
    fields: FieldConfig[],
    variables: VariableConfig[],
    configuration: {
      hideNoValues: boolean;
      horizontalOrientation: boolean;
    },
  ) => {
    const { data } = await getClient().post<{
      pdf: string;
    }>(`api/v1.0/customers/${customerId}/forms/defaultPdfContent`, {
      form: {
        fields: fields.map((field) => toFieldConfigDto(field, fields, variables)),
      },
      mailTriggerConfiguration: {
        hideNoValues: configuration.hideNoValues,
        horizontalOrientation: configuration.horizontalOrientation,

        // Unused, but required properties
        attachments: [],
        attachImages: true,
        attachPdf: true,
        simpleMode: false,
        useHeaderAndFooter: false,
      },
    });
    return data.pdf;
  },
  getDownloadSubmissionFilesUri: async (customerId: CustomerId, formId: FormId, submissionId: SubmissionId) => {
    const { data } = await getClient().get<SecureUriDto>(
      `api/v1.0/customers/${customerId}/forms/${formId}/submissions/${submissionId}/files`,
    );
    return data;
  },
  getExportFields: async (formId: FormId): Promise<ExportField[]> => {
    try {
      const { data } = await getClient().get<ExportField[]>(
        `api/v1.0/customers/${customerId}/forms/${formId.toString()}/submissions/export/fields`,
      );
      return data;
    } catch (e) {
      throw handleApiError(e as AxiosError<MoreAppError>);
    }
  },
  requestExport: async (
    formId: FormId,
    exportFields: ExportField[],
    sendExportToEmailAddress: string,
    filterQueries: Filter[],
    submissionIds: SubmissionId[],
    timezone: string,
    dateFormat: string,
    includeFiles: boolean,
    excelSingleSheet: boolean,
    currentLanguage: LanguageCode,
  ) => {
    try {
      const { data } = await getClient().post<{ id: string }>(
        `api/v1.0/customers/${customerId}/forms/${formId.toString()}/submissions/export`,
        {
          exporterType: "EXCEL",
          mailOnFinish: [sendExportToEmailAddress],
          submissionIds: submissionIds.map((submissionId) => submissionId.toString()),
          exportFields: exportFields,
          filterQueries: filterQueries.map(toApi),
          options: {
            timezone: timezone,
            dateTimeFormat: `${dateFormat} HH:mm:ss`,
            includeFiles: includeFiles,
            excelSingleSheet: excelSingleSheet,
            languageCode: currentLanguage.toUpperCase(),
          },
        },
      );

      return data;
    } catch (e) {
      throw handleApiError(e as AxiosError<MoreAppError>);
    }
  },
  getApiKeys: () =>
    useQuery<ApiKey[]>(() => ({
      queryKey: ["apiKeys"],
      queryFn: async () => {
        const { data } = await getClient().get<ApiKeyDto[]>(`api/v1.0/customers/${customerId}/keys`);
        return data.map(toApiKey);
      },
      throwOnError: true,
    })),
  createApiKey: () => {
    const queryClient = useQueryClient();
    return useMutation<string, MoreAppError, CreateApiKeyRequest>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().post<ApiKeyCreatedDto, AxiosResponse<ApiKeyCreatedDto>, CreateApiKeyRequest>(
          `api/v1.0/customers/${customerId}/keys`,
          {
            name: value.name,
          },
        );

        return data.token;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
      },
    }));
  },
  deleteApiKey: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, ApiKeyId>(() => ({
      mutationFn: async (apiKeyId) => {
        await getClient().delete(`api/v1.0/customers/${customerId}/keys/${apiKeyId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
      },
    }));
  },
  getWebhooks: () =>
    useQuery<Webhook[]>(() => ({
      queryKey: ["webhooks"],
      queryFn: async () => {
        const { data } = await getClient().get<WebhookDto[]>(`api/v1.0/webhooks/customer/${customerId}/subscribers`);
        return data.map(toWebhook);
      },
      throwOnError: true,
    })),
  getWebhook: (webhookId: WebhookId) =>
    useQuery<Webhook>(() => ({
      queryKey: ["webhooks", webhookId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<WebhookDto>(
          `api/v1.0/webhooks/customer/${customerId}/subscribers/${webhookId}`,
        );

        return toWebhook(data);
      },
      staleTime: minutes(10),
      retry: doNotRetryOnUnrecoverableError,
    })),
  createWebhook: () => {
    const queryClient = useQueryClient();
    return useMutation<WebhookDto, MoreAppError, CreateWebhookRequest>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().post<WebhookDto, AxiosResponse<WebhookDto>, CreateWebhookRequest>(
          `api/v1.0/webhooks/customer/${customerId}/subscribers`,
          value,
        );
        return data;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      },
    }));
  },
  updateWebhook: (webhookId: WebhookId) => {
    const queryClient = useQueryClient();
    return useMutation<Webhook, MoreAppError, UpdateWebhookRequest>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().put<WebhookDto, AxiosResponse<WebhookDto>, UpdateWebhookRequest>(
          `api/v1.0/webhooks/customer/${customerId}/subscribers/${webhookId}`,
          value,
        );
        return toWebhook(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      },
    }));
  },
  deleteWebhook: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, WebhookId>(() => ({
      mutationFn: async (webhookId) => {
        await getClient().delete(`api/v1.0/webhooks/customer/${customerId}/subscribers/${webhookId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      },
    }));
  },
  getWebhookInvocations: (webhookId: WebhookId) =>
    useQuery<WebhookInvocation[]>(() => ({
      queryKey: ["webhooks", webhookId.toString(), "invocations"],
      queryFn: async () => {
        const { data } = await getClient().get<WebhookInvocationDto[]>(
          `api/v1.0/webhooks/customer/${customerId}/subscribers/${webhookId}/invocations`,
        );
        return data.map(toWebhookInvocation);
      },
      throwOnError: true,
      staleTime: minutes(1),
    })),
  getEvent: (eventId: EventId) =>
    useQuery<Event>(() => ({
      queryKey: ["events", eventId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<EventDto>(`api/v1.0/webhooks/customer/${customerId}/events/${eventId}`);
        return toEvent(data);
      },
      throwOnError: true,
      staleTime: hours(24),
    })),
  getSsoStatus: () =>
    useQuery<SsoStatus>(() => ({
      queryKey: ["sso"],
      queryFn: async () => {
        const { data } = await getClient().get<SsoStatusDto>(`api/v1.0/customers/${customerId}/sso/status`);
        return toSsoStatus(data);
      },
      throwOnError: true,
      staleTime: minutes(10),
    })),
  getSsoConnectionConfigurationUrl: (type: string, returnUrl: string) =>
    getClient().get<{ url: string }>(
      `api/v1.0/customers/${customerId}/sso/admin-portal/${type}?returnUrl=${returnUrl}`,
    ),
  provisionOrganization: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, void>(() => ({
      mutationFn: async () => {
        await getClient().post(`api/v1.0/customers/${customerId}/sso/provision`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["sso"] });
      },
    }));
  },
  getEmailConfiguration: () =>
    useQuery<EmailConfiguration>(() => ({
      queryKey: ["emailConfiguration"],
      queryFn: async () => {
        const { data } = await getClient().get<EmailConfigurationDto>(
          `api/v1.0/customers/${customerId}/sending-domain`,
        );
        return toEmailConfiguration(data);
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  createEmailConfiguration: () => {
    const queryClient = useQueryClient();
    return useMutation<EmailConfiguration, MoreAppError, CreateEmailConfigurationRequest>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().post<
          EmailConfigurationDto,
          AxiosResponse<EmailConfigurationDto>,
          CreateEmailConfigurationRequest
        >(`api/v1.0/customers/${customerId}/sending-domain`, value);
        return toEmailConfiguration(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["emailConfiguration"] });
      },
    }));
  },
  updateEmailConfiguration: () => {
    const queryClient = useQueryClient();
    return useMutation<EmailConfiguration, MoreAppError, UpdateEmailConfigurationRequest>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().put<
          EmailConfigurationDto,
          AxiosResponse<EmailConfigurationDto>,
          UpdateEmailConfigurationRequest
        >(`api/v1.0/customers/${customerId}/sending-domain`, value);
        return toEmailConfiguration(data);
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["emailConfiguration"], data);
      },
    }));
  },
  verifyEmailConfiguration: () => {
    const queryClient = useQueryClient();
    return useMutation<EmailConfiguration, MoreAppError, void>(() => ({
      mutationFn: async () => {
        const { data } = await getClient().post<EmailConfigurationDto>(
          `api/v1.0/customers/${customerId}/sending-domain/verify`,
        );
        return toEmailConfiguration(data);
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["emailConfiguration"], data);
      },
    }));
  },
  deleteEmailConfiguration: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError>(() => ({
      mutationFn: async () => {
        await getClient().delete(`api/v1.0/customers/${customerId}/sending-domain`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["emailConfiguration"] });
      },
      throwOnError: true,
    }));
  },
  getLocationConfiguration: () =>
    useQuery<LocationConfiguration>(() => ({
      queryKey: ["locationConfiguration"],
      queryFn: async () => {
        const { data } = await getClient().get<LocationConfiguration>(`${LOCATION_BASE_URL}/v2/location/configuration`);
        return data;
      },
      staleTime: hours(24),
    })),
  getReverseLocation: async (coordinates: ApiCoordinates): Promise<LocationResult> =>
    await getClient()
      .get<ReversedResult>(`${LOCATION_BASE_URL}/v2/location/reverse`, {
        params: coordinates,
      })
      .then(({ data }) => {
        const result = {
          coordinates: {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          },
          ...(!isNil(data.results[0])
            ? {
                location: {
                  descriptiveName: data.results[0].descriptiveName,
                  road: data.results[0].road,
                  houseNumber: data.results[0].houseNumber,
                  city: data.results[0].city,
                  postcode: data.results[0].postcode,
                  country: data.results[0].country,
                },
              }
            : {}),
        };
        return { ...result, formattedValue: locationToHumanReadableString(result) };
      }),
  getPreviewToken: async (formId: FormId): Promise<string> => {
    const { data } = await getClient().get<string>(`api/v1.0/customers/${customerId}/preview/${formId}`);
    return data;
  },
  createTask: () => {
    const queryClient = useQueryClient();
    return useMutation<unknown, MoreAppError, CreateTaskRequest>(() => ({
      mutationFn: async ({ formId, recipients, message, publishInfo, informationDate, location, registrationData }) => {
        const { data } = await getClient().post(`api/v1.0/customers/${customerId}/${formId}/tasks`, {
          message,
          recipients: recipients.map(({ kind, id }) => ({ type: kind, value: id.toString() })),
          publishInfo,
          ...(!isNil(location) ? { location } : {}),
          data: registrationData,
          ...(!isNil(informationDate) ? { informationDate } : {}),
        });
        return data;
      },
      onSuccess: (_, { formId }) => {
        void queryClient.invalidateQueries({
          queryKey: ["forms", formId.toString(), "tasks"],
          exact: false,
        });
      },
    }));
  },
  getDataSources: () =>
    useQuery(() => ({
      queryKey: ["dataSources"],
      queryFn: async () => {
        const { data } = await getClient().get<DataSourceDto[]>(`api/v1.0/customers/${customerId}/datasources`);
        return data.map(toDataSource);
      },
      staleTime: minutes(10),
      throwOnError: true,
    })),
  getDataSource: (dataSourceId: DataSourceId) =>
    useQuery(() => ({
      queryKey: ["dataSources", dataSourceId.toString()],
      queryFn: async () => {
        const { data } = await getClient().get<DataSourceDto>(
          `api/v1.0/customers/${customerId}/datasources/${dataSourceId}`,
        );
        return toDataSource(data);
      },
      staleTime: minutes(10),
      retry: doNotRetryOnUnrecoverableError,
    })),
  enableDataSource: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, DataSourceId>(() => ({
      mutationFn: async (dataSourceId) => {
        await getClient().post(`api/v1.0/customers/${customerId}/datasources/${dataSourceId}/enable`);
      },
      onSuccess: (_, dataSourceId) => {
        void queryClient.invalidateQueries({ queryKey: ["dataSources", dataSourceId.toString()] });
      },
    }));
  },
  scheduleDataSource: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, DataSourceId>(() => ({
      mutationFn: async (dataSourceId) => {
        await getClient().post(`api/v1.0/customers/${customerId}/datasources/${dataSourceId}/schedule`);
      },
      onSuccess: (_, dataSourceId) => {
        void queryClient.invalidateQueries({ queryKey: ["dataSources", dataSourceId.toString()] });
      },
    }));
  },
  getDataSourceStatus: (dataSourceId: DataSourceId) => {
    const queryClient = useQueryClient();
    return useQuery(() => ({
      queryKey: ["dataSources", dataSourceId.toString(), "status"],
      queryFn: async () => {
        const { data } = await getClient().get<{ inProgress: boolean }>(
          `api/v1.0/customers/${customerId}/datasources/${dataSourceId}/status`,
        );

        const previousData = queryClient.getQueryData<{ inProgress: boolean }>([
          "dataSources",
          dataSourceId.toString(),
          "status",
        ]);

        // Invalidate cache when import has finished
        if (previousData?.inProgress === true && data.inProgress === false) {
          // Invalidate specific queries to avoid invalidating the status query
          void queryClient.invalidateQueries({ queryKey: ["dataSources", dataSourceId.toString()], exact: true });
          void queryClient.invalidateQueries({ queryKey: ["dataSources", dataSourceId.toString(), "entries"] });
        }

        return data;
      },
      refetchInterval: (query) => (query.state.data?.inProgress === true ? seconds(2) : false),
    }));
  },
  deleteDataSource: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, DataSourceId>(() => ({
      mutationFn: async (dataSourceId) => {
        await getClient().delete(`api/v1.0/customers/${customerId}/datasources/${dataSourceId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["dataSources"] });
        void queryClient.invalidateQueries({ queryKey: ["dataSourceInfos"] });
      },
    }));
  },
  getDataSourceInfosQuery: (formId: FormId, enabled?: Accessor<boolean>) =>
    useQuery(() => ({ ...dataSourceInfosQueryOptions(formId), enabled: enabled?.() ?? true })),
  getDataSourceInfo: async (id: string, formId: FormId) => {
    const queryClient = useQueryClient();
    const dataSourceInfos = await queryClient.fetchQuery(dataSourceInfosQueryOptions(formId));
    return dataSourceInfos.find((ds) => ds.id === id);
  },
  updateCustomerName: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, string>(() => ({
      mutationFn: async (name) => {
        await getClient().put<void, AxiosResponse<void>, { name: string }>(`api/v1.0/customers/${customerId}`, {
          name,
        });
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["customer"] });
      },
    }));
  },
  updateCustomerSettings: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, UpdateCustomerSettingsRequest>(() => ({
      mutationFn: async (value) => {
        await getClient().put<void, AxiosResponse<void>, { settings: UpdateCustomerSettingsRequest }>(
          `api/v1.0/customers/${customerId}`,
          {
            settings: value,
          },
        );
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["customer"] });
      },
    }));
  },
  updateCustomerLogo: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, string>(() => ({
      mutationFn: async (value) => {
        await getClient().put(`api/v1.0/customers/${customerId}/logo/${value}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["customer"] });
      },
    }));
  },
  removeCustomerLogo: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, void>(() => ({
      mutationFn: async () => {
        await getClient().delete(`api/v1.0/customers/${customerId}/logo`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["customer"] });
      },
    }));
  },
  scheduleCustomerDeletion: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, ScheduleCustomerDeletionRequest>(() => ({
      mutationFn: async (value) => {
        await getClient().post(`api/v1.0/customers/${customerId}/schedule-delete`, value);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["customer"] });
      },
    }));
  },
  init: init,
};

export default ApiService;
